import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const CreateChallengeModal = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'strength',
    difficulty: 'medium',
    target_value: '',
    metric_unit: 'workouts',
    start_date: '',
    end_date: '',
    rewards: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const categories = [
    { value: 'strength', label: 'Strength Training' },
    { value: 'cardio', label: 'Cardio' },
    { value: 'endurance', label: 'Endurance' },
    { value: 'weight_loss', label: 'Weight Loss' },
    { value: 'consistency', label: 'Consistency' }
  ];

  const difficulties = [
    { value: 'easy', label: 'Easy' },
    { value: 'medium', label: 'Medium' },
    { value: 'hard', label: 'Hard' }
  ];

  const metricUnits = [
    { value: 'workouts', label: 'Workouts' },
    { value: 'minutes', label: 'Minutes' },
    { value: 'calories', label: 'Calories' },
    { value: 'kg', label: 'Kilograms' },
    { value: 'reps', label: 'Repetitions' },
    { value: 'days', label: 'Days' }
  ];

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.title?.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData?.description?.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData?.target_value || formData?.target_value <= 0) {
      newErrors.target_value = 'Target value must be greater than 0';
    }
    if (!formData?.start_date) {
      newErrors.start_date = 'Start date is required';
    }
    if (!formData?.end_date) {
      newErrors.end_date = 'End date is required';
    }
    if (formData?.start_date && formData?.end_date && new Date(formData?.end_date) <= new Date(formData?.start_date)) {
      newErrors.end_date = 'End date must be after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await onCreate({
        ...formData,
        target_value: parseFloat(formData?.target_value)
      });
    } catch (error) {
      console.error('Error creating challenge:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h2 className="text-2xl font-heading font-bold text-foreground">
            Create New Challenge
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-smooth"
            aria-label="Close"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Challenge Title *
            </label>
            <Input
              name="title"
              value={formData?.title}
              onChange={handleChange}
              placeholder="e.g., 30-Day Workout Streak"
              className={errors?.title ? 'border-destructive' : ''}
            />
            {errors?.title && (
              <p className="mt-1 text-sm text-destructive">{errors?.title}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Description *
            </label>
            <textarea
              name="description"
              value={formData?.description}
              onChange={handleChange}
              placeholder="Describe the challenge goals and rules..."
              rows={3}
              className={`w-full px-4 py-2 bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                errors?.description ? 'border-destructive' : 'border-border'
              }`}
            />
            {errors?.description && (
              <p className="mt-1 text-sm text-destructive">{errors?.description}</p>
            )}
          </div>

          {/* Category and Difficulty */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Category
              </label>
              <select
                name="category"
                value={formData?.category}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {categories?.map(cat => (
                  <option key={cat?.value} value={cat?.value}>
                    {cat?.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Difficulty
              </label>
              <select
                name="difficulty"
                value={formData?.difficulty}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {difficulties?.map(diff => (
                  <option key={diff?.value} value={diff?.value}>
                    {diff?.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Target Value and Metric */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Target Value *
              </label>
              <Input
                type="number"
                name="target_value"
                value={formData?.target_value}
                onChange={handleChange}
                placeholder="e.g., 30"
                className={errors?.target_value ? 'border-destructive' : ''}
              />
              {errors?.target_value && (
                <p className="mt-1 text-sm text-destructive">{errors?.target_value}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Metric Unit
              </label>
              <select
                name="metric_unit"
                value={formData?.metric_unit}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {metricUnits?.map(unit => (
                  <option key={unit?.value} value={unit?.value}>
                    {unit?.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Start and End Dates */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Start Date *
              </label>
              <Input
                type="date"
                name="start_date"
                value={formData?.start_date}
                onChange={handleChange}
                className={errors?.start_date ? 'border-destructive' : ''}
              />
              {errors?.start_date && (
                <p className="mt-1 text-sm text-destructive">{errors?.start_date}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                End Date *
              </label>
              <Input
                type="date"
                name="end_date"
                value={formData?.end_date}
                onChange={handleChange}
                className={errors?.end_date ? 'border-destructive' : ''}
              />
              {errors?.end_date && (
                <p className="mt-1 text-sm text-destructive">{errors?.end_date}</p>
              )}
            </div>
          </div>

          {/* Rewards */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Rewards (Optional)
            </label>
            <Input
              name="rewards"
              value={formData?.rewards}
              onChange={handleChange}
              placeholder="e.g., Free personal training session"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              className="flex-1"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <Icon name="Loader2" size={18} className="animate-spin" />
              ) : (
                'Create Challenge'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateChallengeModal;